import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import {
  CalendarDays,
  MapPin,
  Search,
  Ticket,
  Loader2,
  ArrowRight,
  Filter,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { eventsApi, formatAuDate, centsToAUD, coverImageUrl, EVENT_CATEGORIES, categoryLabel } from "../lib/events";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

function priceFromCents(event) {
  const tt = event.ticket_types || [];
  if (!tt.length) return null;
  const paid = tt.filter((t) => (t.price_cents || 0) > 0);
  if (paid.length === 0) return 0;
  return Math.min(...paid.map((t) => t.price_cents));
}

function availabilityLabel(event) {
  const tt = event.ticket_types || [];
  const totalAvail = tt.reduce((s, t) => s + (t.available ?? 0), 0);
  const totalQty = tt.reduce((s, t) => s + (t.quantity ?? 0), 0);
  if (totalQty === 0) return { label: "Coming soon", tone: "muted" };
  if (totalAvail === 0) return { label: "Sold out", tone: "sold" };
  if (totalAvail <= Math.max(10, Math.round(totalQty * 0.1))) {
    return { label: "Limited availability", tone: "low" };
  }
  return { label: "On sale", tone: "ok" };
}

function EventCard({ event, isPast = false }) {
  const cover = coverImageUrl(event);
  const priceCents = priceFromCents(event);
  const isFree = priceCents === 0;
  const avail = isPast ? { label: "Event ended", tone: "sold" } : availabilityLabel(event);
  const cat = categoryLabel(event.category);

  const availClass =
    avail.tone === "sold"
      ? "text-[var(--gt-ink-3)]"
      : avail.tone === "low"
        ? "text-[var(--gt-accent)]"
        : avail.tone === "ok"
          ? "text-[var(--gt-ink-2)]"
          : "text-[var(--gt-ink-3)]";

  return (
    <Link
      to={`/e/${event.slug}`}
      data-testid={`public-event-card-${event.slug}`}
      className="group flex flex-col border border-[var(--gt-line)] rounded-sm bg-white overflow-hidden hover:border-[var(--gt-ink)] transition-colors"
    >
      <div className="relative aspect-[16/9] bg-[var(--gt-bg-surface)] overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt=""
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)]">
            <CalendarDays size={40} strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {isPast && (
            <span
              className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm bg-[var(--gt-ink)] text-white"
              data-testid={`public-event-ended-badge-${event.slug}`}
            >
              Ended
            </span>
          )}
          <span
            className={`inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm ${
              isFree
                ? "bg-[var(--gt-ink)] text-white"
                : "bg-white/95 text-[var(--gt-ink)] border border-[var(--gt-line)]"
            }`}
          >
            {isFree ? "Free" : "Paid"}
          </span>
          {cat && (
            <span
              className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)] border border-[var(--gt-accent)]/30"
              data-testid={`public-event-category-${event.slug}`}
            >
              {cat}
            </span>
          )}
        </div>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <h3 className="font-heading text-xl font-bold tracking-tight leading-tight group-hover:text-[var(--gt-accent)] transition-colors">
          {event.title}
        </h3>
        <div className="flex flex-col gap-1.5 text-xs text-[var(--gt-ink-2)]">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={13} /> {formatAuDate(event.start_at)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={13} /> {event.venue_name}, {event.state}
          </span>
          <span className={`inline-flex items-center gap-1.5 font-medium ${availClass}`}>
            <Ticket size={13} /> {avail.label}
          </span>
        </div>
        <div className="mt-auto flex items-end justify-between pt-3 border-t border-[var(--gt-line)]">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-[var(--gt-ink-3)]">
              {isFree ? "Entry" : "From"}
            </div>
            <div className="font-heading font-black text-lg">
              {isFree ? "Free" : centsToAUD(priceCents)}
            </div>
          </div>
          <span className="text-xs font-medium text-[var(--gt-ink)] inline-flex items-center gap-1 group-hover:text-[var(--gt-accent)]">
            {isPast ? "View event" : isFree ? "Register" : "Get tickets"} <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function PublicEvents() {
  const [events, setEvents] = useState(null);
  const [pastEvents, setPastEvents] = useState(null);
  const [timeTab, setTimeTab] = useState("upcoming"); // upcoming | past
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [priceFilter, setPriceFilter] = useState("all"); // all | free | paid
  const [category, setCategory] = useState("");

  useDocumentMeta({
    title: "Browse events · Getickets",
    description:
      "Discover free and paid events across every Australian state. Live music, comedy, food & wine, workshops, sport and more. All prices in AUD.",
    url: typeof window !== "undefined" ? window.location.origin + "/events" : undefined,
    type: "website",
  });

  useEffect(() => {
    let alive = true;
    eventsApi
      .publicList()
      .then((data) => alive && setEvents(data))
      .catch(() => alive && setError("Could not load events. Please try again shortly."));
    eventsApi
      .publicList({ time: "past" })
      .then((data) => alive && setPastEvents(data))
      .catch(() => alive && setPastEvents([]));
    return () => {
      alive = false;
    };
  }, []);

  const source = timeTab === "past" ? pastEvents : events;

  const filtered = useMemo(() => {
    if (!source) return [];
    return source
      .filter((e) => {
        if (!q.trim()) return true;
        const needle = q.trim().toLowerCase();
        return (
          (e.title || "").toLowerCase().includes(needle) ||
          (e.venue_name || "").toLowerCase().includes(needle) ||
          (e.description || "").toLowerCase().includes(needle) ||
          (e.state || "").toLowerCase().includes(needle)
        );
      })
      .filter((e) => (state ? e.state === state : true))
      .filter((e) => (category ? e.category === category : true))
      .filter((e) => {
        if (priceFilter === "all") return true;
        const pc = priceFromCents(e);
        if (priceFilter === "free") return pc === 0;
        return pc && pc > 0;
      })
      .sort((a, b) =>
        timeTab === "past"
          ? new Date(b.start_at) - new Date(a.start_at)
          : new Date(a.start_at) - new Date(b.start_at),
      );
  }, [source, timeTab, q, state, priceFilter, category]);

  const isLoading = source === null && !error;
  const isEmpty = source && filtered.length === 0;

  return (
    <div
      className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)]"
      data-testid="public-events-page"
    >
      <nav className="gt-glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/events"
              data-testid="nav-browse-events-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Browse events
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Pricing
            </Link>
            <Link
              to="/who-we-are"
              data-testid="nav-who-we-are-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Who We Are
            </Link>
            <Link
              to="/resend"
              data-testid="nav-resend-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Lost ticket?
            </Link>
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="gt-btn-ghost text-sm"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup-btn"
              className="gt-btn-primary text-sm py-2"
            >
              Sign up
            </Link>
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-12 sm:pt-16 pb-6">
        <div className="gt-label mb-4" data-testid="events-eyebrow">
          What&apos;s on in Australia
        </div>
        <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-4">
          {timeTab === "past" ? "Past events" : "Browse upcoming events"}
          <span className="text-[var(--gt-accent)]">.</span>
        </h1>
        <p className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-2xl">
          {timeTab === "past"
            ? "Events that have already wrapped up. Viewable for reference — tickets are no longer on sale."
            : "Discover free and paid events across every state — sorted by whichever is happening next. All prices in AUD, all dates in DD/MM/YYYY."}
        </p>
        <div className="flex gap-2 mt-6" data-testid="events-time-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={timeTab === "upcoming"}
            onClick={() => setTimeTab("upcoming")}
            data-testid="events-tab-upcoming"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
              timeTab === "upcoming"
                ? "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]"
                : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)] hover:border-[var(--gt-ink)]"
            }`}
          >
            Upcoming
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={timeTab === "past"}
            onClick={() => setTimeTab("past")}
            data-testid="events-tab-past"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
              timeTab === "past"
                ? "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]"
                : "bg-white text-[var(--gt-ink-2)] border-[var(--gt-line)] hover:border-[var(--gt-ink)]"
            }`}
          >
            Past{pastEvents?.length ? ` (${pastEvents.length})` : ""}
          </button>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-8 sm:pb-10">
        <div
          className="flex flex-col md:flex-row gap-3 md:items-center md:gap-4 bg-white border border-[var(--gt-line)] rounded-sm p-3 sm:p-4"
          data-testid="events-filters"
        >
          <div className="flex-1 flex items-center gap-2 border border-[var(--gt-line)] rounded-sm px-3 py-2 focus-within:border-[var(--gt-ink)]">
            <Search size={16} className="text-[var(--gt-ink-3)]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, venue, or state"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--gt-ink-3)]"
              data-testid="events-search-input"
              aria-label="Search events"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-[var(--gt-ink-3)] hidden sm:inline" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border border-[var(--gt-line)] rounded-sm px-3 py-2 text-sm bg-white focus:border-[var(--gt-ink)] outline-none"
              data-testid="events-category-filter"
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {EVENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="border border-[var(--gt-line)] rounded-sm px-3 py-2 text-sm bg-white focus:border-[var(--gt-ink)] outline-none"
              data-testid="events-state-filter"
              aria-label="Filter by state"
            >
              <option value="">All states</option>
              {AU_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              className="border border-[var(--gt-line)] rounded-sm px-3 py-2 text-sm bg-white focus:border-[var(--gt-ink)] outline-none"
              data-testid="events-price-filter"
              aria-label="Filter by price"
            >
              <option value="all">All prices</option>
              <option value="free">Free only</option>
              <option value="paid">Paid only</option>
            </select>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {isLoading && (
          <div
            className="flex items-center gap-3 text-[var(--gt-ink-2)]"
            data-testid="public-events-loading"
          >
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading events…</span>
          </div>
        )}

        {error && (
          <div
            className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]"
            data-testid="public-events-error"
          >
            {error}
          </div>
        )}

        {!isLoading && !error && isEmpty && (
          <div
            className="border border-[var(--gt-line)] rounded-sm p-12 text-center bg-white"
            data-testid="public-events-empty"
          >
            <div className="gt-label mb-3">No matches</div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">
              {timeTab === "past"
                ? "No past events to show."
                : "No public events match your search."}
            </h3>
            <p className="text-[var(--gt-ink-2)] text-sm max-w-md mx-auto">
              {timeTab === "past"
                ? "Once events wrap up, they'll appear here for reference."
                : "Try clearing your filters or check back soon — Aussie organisers publish new events every day."}
            </p>
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <>
            <div
              className="text-xs font-mono uppercase tracking-widest text-[var(--gt-ink-3)] mb-4"
              data-testid="public-events-count"
            >
              Showing {filtered.length} event{filtered.length === 1 ? "" : "s"}
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="public-events-grid"
            >
              {filtered.map((e) => (
                <EventCard key={e.id} event={e} isPast={timeTab === "past"} />
              ))}
            </div>
          </>
        )}

        {/* Past events preview under the upcoming list */}
        {timeTab === "upcoming" && !isLoading && !error && (pastEvents || []).length > 0 && (
          <div className="mt-16" data-testid="past-events-section">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
              <h2 className="font-heading text-lg md:text-lg font-bold tracking-tight">
                Past events
              </h2>
              <button
                type="button"
                onClick={() => {
                  setTimeTab("past");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-xs font-semibold uppercase tracking-widest underline underline-offset-4 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
                data-testid="view-all-past-events-btn"
              >
                View all past events ({pastEvents.length})
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="past-events-preview-grid">
              {pastEvents.slice(0, 3).map((e) => (
                <EventCard key={e.id} event={e} isPast />
              ))}
            </div>
          </div>
        )}
      </section>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[var(--gt-ink-3)] text-xs tracking-widest uppercase" data-testid="footer-copyright">
            © {new Date().getFullYear()} Getickets · Made for Aussie organisers. Powered by{" "}
            <a
              href="https://getupsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--gt-accent)]"
              data-testid="footer-powered-by-link"
            >
              www.getupsolutions.com.au
            </a>
          </div>
          <div className="flex gap-4 text-xs">
            <Link to="/" className="hover:text-[var(--gt-accent)]" data-testid="footer-home-link">Home</Link>
            <Link to="/who-we-are" className="hover:text-[var(--gt-accent)]" data-testid="footer-who-we-are-link">Who we are</Link>
            <Link to="/privacy" className="hover:text-[var(--gt-accent)]" data-testid="footer-privacy-link">Privacy</Link>
            <ManageCookiesLink className="text-xs" />
            <a className="hover:text-[var(--gt-accent)]" href="mailto:admin@getickets.com.au">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
